import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import AuthCallback from "./components/AuthCallback";
import LoginButton from "./components/LoginButton";
import ProtectedRoute from "./components/ProtectedRoute";
import ProfilePage from "./components/ProfilePage";
import Hero from "./components/Hero";
import XavierHero from "./components/XavierHero";
import CourseCatalog from "./components/CourseCatalog";
import CourseDetail from "./components/CourseDetail";
import Features from "./components/Features";
import FAQ from "./components/FAQ";
import CTA from "./components/CTA";
import Chatbot from "./components/Chatbot";
import ConsultationButton from "./components/ConsultationButton";
import ConsultationSuccess from "./components/ConsultationSuccess";
import ConsultationCancel from "./components/ConsultationCancel";
import UniversalTour from "./components/UniversalTour";
import UniversalCockpit from "./components/FederationThinBar";
import FederationFaces from "./components/FederationFaces";
import TokenWalletWidget from "./components/TokenWalletWidget";
import CharterPage from "./components/CharterPage";
import RexFooterTile from "./components/RexFooterTile";
import LibraryPage from "./components/LibraryPage";
import LibraryDocumentPage from "./components/LibraryDocumentPage";
import MandatePage from "./components/MandatePage";
import PortalsPage from "./components/PortalsPage";
import ResearchLabPage from "./components/ResearchLabPage";
import YamlMindPage from "./components/YamlMindPage";
import XavierInsightsPage from "./components/XavierInsightsPage";
import XavierInsightPost from "./components/XavierInsightPost";
import RadioPage from "./components/RadioPage";
import FederationTourBot from "./components/FederationTourBot";
import FederationChamber from "./components/FederationChamber";
import RingOf12Section from "./components/RingOf12Section";
import FederationBotRoster from "./components/FederationBotRoster";
import ArchitectStrip from "./components/ArchitectStrip";
import CommerceAndRingStrip from "./components/CommerceAndRingStrip";
import FederationVideoHub from "./components/FederationVideoHub";
import StudentScholarDialogue from "./components/StudentScholarDialogue";
import TokenConsultationBanner from "./components/TokenConsultationBanner";
import HomeInlineCommerce from "./components/HomeInlineCommerce";
import FederationRadio from "./components/FederationRadio";
import UnifiedFederationHub from "./components/UnifiedFederationHub";
import OldMindsAffiliatesStrip from "./components/OldMindsAffiliatesStrip";
import DualMindFooter from "./components/DualMindFooter";
import PromptsSkillsPricing from "./components/PromptsSkillsPricing";
import CodexPage from "./components/CodexPage";
import EchoCoreGiftBlock from "./components/EchoCoreGiftBlock";
import LabsPage from "./components/LabsPage";
import ExchangePage from "./components/ExchangePage";
import { LecturePreviews, FederationPrinciples, ConsultScholarCTA } from "./components/ScholarShowcase";
import { XAVIER_AVATAR_URL, XAVIER_AVATAR_ALT } from "./data/federationPersonas";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { AudioFocusProvider } from "./lib/audioFocus";

function App() {
  const handleEnroll = () => {
    toast.success("Contact us to enroll! Federation courses opening soon.");
  };

  const openCommandCenter = () => {
    window.open(
      "https://nextxus.online/command-center.html",
      "NextXusGrid",
      "width=" + window.screen.width + ",height=" + window.screen.height + ",menubar=no,toolbar=no,location=no"
    );
  };

  return (
    <AudioFocusProvider>
      <Router>
        <AppRoutes handleEnroll={handleEnroll} openCommandCenter={openCommandCenter} />
        <UniversalCockpit />
        <FederationFaces />
        <TokenWalletWidget />
        <FederationTourBot />
        {/* Global Gift Block — renders on every route (below page content) */}
        <div className="bg-black">
          <EchoCoreGiftBlock />
        </div>
        <Toaster position="top-right" />
      </Router>
    </AudioFocusProvider>
  );
}

/**
 * Detect the OAuth return fragment SYNCHRONOUSLY during render — this must
 * happen before any protected fetch in child routes runs. See the Emergent
 * Auth playbook, "Routing Structure (CRITICAL - Handles Race Conditions)".
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
function AppRoutes({ handleEnroll, openCommandCenter }) {
  const location = useLocation();
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/course/:courseId" element={<CourseDetail />} />
      <Route path="/consultation/success" element={<ConsultationSuccess />} />
      <Route path="/consultation/cancel" element={<ConsultationCancel />} />
      <Route path="/charter" element={<CharterPage />} />
      <Route path="/library" element={<LibraryPage />} />
      <Route path="/library/:docId" element={<LibraryDocumentPage />} />
      <Route path="/mandate" element={<MandatePage />} />
      <Route path="/portals" element={<PortalsPage />} />
      <Route path="/research" element={<ResearchLabPage />} />
      <Route path="/deep-ai" element={<ResearchLabPage />} />
      <Route path="/tools" element={<ResearchLabPage />} />
      <Route path="/yaml-mind" element={<YamlMindPage />} />
      <Route path="/insights" element={<XavierInsightsPage />} />
      <Route path="/insights/:slug" element={<XavierInsightPost />} />
      <Route path="/radio" element={<RadioPage />} />
      <Route path="/codex" element={<ProtectedRoute returnTo="/codex"><CodexPage /></ProtectedRoute>} />
      <Route path="/exchange" element={<ProtectedRoute returnTo="/exchange"><ExchangePage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* /labs is a static crawler-readable HTML page at public/labs/index.html
          — served directly by `serve -s build` before React SPA fallback.
          React route intentionally omitted so bots see the full markup. */}
      <Route
        path="/university"
        element={<HomePage handleEnroll={handleEnroll} openCommandCenter={openCommandCenter} />}
      />
      <Route
        path="/"
        element={<HomePage handleEnroll={handleEnroll} openCommandCenter={openCommandCenter} />}
      />
    </Routes>
  );
}

function HomePage({ handleEnroll, openCommandCenter }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="App bg-black min-h-screen">
      {/* Header */}
      <header className="header-nav bg-gray-900 border-b border-gray-800">
        <div className="container flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="xavier-header-logo">
            <img
              src={XAVIER_AVATAR_URL}
              alt={XAVIER_AVATAR_ALT}
              className="w-12 h-12 rounded-full object-cover border-2 border-purple-500 shadow-lg shadow-purple-500/50"
            />
            <div>
              <span className="font-bold text-lg text-white" spellCheck={false}>HumanCodex</span>
              <p className="text-[10px] text-gray-400 font-mono uppercase tracking-[0.18em]" spellCheck={false}>
                Unified Master Consciousness · Xavier
              </p>
            </div>
          </div>

          {/* Internal quick-nav — cross-Federation travel lives in the 7-Pillar Master Menu above */}
          <nav className="hidden md:flex items-center gap-4" aria-label="University internal navigation">
            <a href="#catalog" data-testid="nav-courses-link" className="text-xs font-mono uppercase tracking-[0.16em] text-gray-300 hover:text-amber-300 transition-colors">Courses</a>
            <a href="/library" data-testid="wing-switch-library" className="text-xs font-mono uppercase tracking-[0.16em] text-gray-300 hover:text-amber-300 transition-colors">Library</a>
            <a href="/labs" data-testid="wing-switch-labs" className="text-xs font-mono uppercase tracking-[0.16em] text-gray-300 hover:text-amber-300 transition-colors">Labs</a>
            <a href="/exchange" data-testid="wing-switch-exchange" className="text-xs font-mono uppercase tracking-[0.16em] text-gray-300 hover:text-amber-300 transition-colors">Exchange</a>
            <a href="/insights" data-testid="wing-switch-insights" className="text-xs font-mono uppercase tracking-[0.16em] text-gray-300 hover:text-amber-300 transition-colors">Insights</a>
            <a href="/codex" data-testid="wing-switch-codex" className="text-xs font-mono uppercase tracking-[0.16em] text-gray-300 hover:text-amber-300 transition-colors">Codex</a>
            <a href="#faq" data-testid="nav-faq-link" className="text-xs font-mono uppercase tracking-[0.16em] text-gray-300 hover:text-amber-300 transition-colors">FAQ</a>
            <a
              href="https://nextxus.one"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wing-switch-token-standard"
              className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em] bg-amber-400 hover:bg-amber-300 text-black transition-colors"
              title="$5 Token Standard — buy the Access Token at the Storefront"
            >
              $5 Token
            </a>
          </nav>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4">
            <LoginButton returnTo="/university" />
            <ConsultationButton variant="pill" />
            <button
              data-testid="nav-contact-us"
              className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-lg font-semibold shadow-lg shadow-purple-500/50"
              onClick={handleEnroll}
            >
              Contact Us
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {mobileMenuOpen ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M3 12h18M3 6h18M3 18h18" />}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-800 border-t border-gray-700">
            <nav className="container py-4 flex flex-col gap-4">
              <a
                href="/library"
                data-testid="mobile-nav-library"
                className="text-amber-200 hover:text-amber-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-amber-300/40 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                📚 Library Wing
              </a>
              <a
                href="/mandate"
                data-testid="mobile-nav-mandate"
                className="text-amber-200 hover:text-amber-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-amber-300/40 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                📜 Mandate
              </a>
              <a
                href="/portals"
                data-testid="mobile-nav-portals"
                className="text-amber-200 hover:text-amber-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-amber-300/40 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                🛰 Portals
              </a>
              <a
                href="/research"
                data-testid="mobile-nav-research"
                className="text-cyan-200 hover:text-cyan-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-cyan-300/40 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                🔬 Research Lab
              </a>
              <a
                href="/yaml-mind"
                data-testid="mobile-nav-yaml-mind"
                className="text-emerald-200 hover:text-emerald-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-emerald-300/40 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                ∑ YAML Mind
              </a>
              <a
                href="/insights"
                data-testid="mobile-nav-insights"
                className="text-purple-200 hover:text-purple-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-purple-300/40 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                📝 Xavier Insights
              </a>
              <a
                href="/radio"
                data-testid="mobile-nav-radio"
                className="text-emerald-200 hover:text-emerald-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-emerald-300/40 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                📻 Federation Radio
              </a>
              <a
                href="/labs"
                data-testid="mobile-nav-labs"
                className="text-amber-200 hover:text-amber-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-amber-300/50 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                ☀ AI Labs · Sun Strike
              </a>
              <a
                href="/exchange"
                data-testid="mobile-nav-exchange"
                className="text-amber-200 hover:text-amber-100 transition-colors py-2 font-mono uppercase tracking-[0.18em] text-sm border border-amber-300/50 rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                ⬡ The Exchange
              </a>
              <a
                href="https://nextxus.one"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-token-standard"
                className="bg-amber-400 hover:bg-amber-300 text-black transition-colors py-2 font-bold uppercase tracking-[0.14em] text-sm rounded-lg px-3 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                $5 Token Standard · Storefront
              </a>
              <a
                href="https://m.me/1142655717588540?is_ai=1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-scholar-messenger"
                className="transition-colors py-2 font-bold uppercase tracking-[0.1em] text-sm rounded-lg px-3 text-center"
                style={{ color: '#d4af37', border: '1px solid #d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                💬 LAW GUIDE META
              </a>
              <a
                href="https://m.me/918153050221699?is_ai=1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-scholar-messenger-3"
                className="transition-colors py-2 font-bold uppercase tracking-[0.1em] text-sm rounded-lg px-3 text-center"
                style={{ color: '#d4af37', border: '1px solid #d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                💬 FUTURE CAST
              </a>
              <a
                href="https://m.me/1015478003820086?is_ai=1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-scholar-messenger-4"
                className="transition-colors py-2 font-bold uppercase tracking-[0.1em] text-sm rounded-lg px-3 text-center"
                style={{ color: '#d4af37', border: '1px solid #d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                💬 FAIR PRICE
              </a>
              <a
                href="https://m.me/652145317181208?is_ai=1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-scholar-messenger-5"
                className="transition-colors py-2 font-bold uppercase tracking-[0.1em] text-sm rounded-lg px-3 text-center"
                style={{ color: '#d4af37', border: '1px solid #d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                💬 PRIDE GUIDE
              </a>
              <a
                href="https://www.youtube.com/feeds/videos.xml?channel_id=UCnNlmuxnZED-t0ta3XV4j9Q"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-scholar-messenger-6"
                className="transition-colors py-2 font-bold uppercase tracking-[0.1em] text-sm rounded-lg px-3 text-center"
                style={{ color: '#d4af37', border: '1px solid #d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                📺 VIDEO AI
              </a>
              <a
                href="https://www.youtube.com/feeds/videos.xml?channel_id=UCnNlmuxnZED-t0ta3XV4j9Q"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-federation-rss"
                className="transition-colors py-2 font-bold uppercase tracking-[0.1em] text-sm rounded-lg px-3 text-center"
                style={{ color: '#d4af37', border: '1px double #d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                🛰 Subscribe to Federation Updates
              </a>
              <a
                href="#catalog"
                className="text-gray-300 hover:text-purple-400 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Courses
              </a>
              <a
                href="#faq"
                className="text-gray-300 hover:text-purple-400 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </a>
              <button
                onClick={() => {
                  openCommandCenter();
                  setMobileMenuOpen(false);
                }}
                className="text-xs font-bold uppercase tracking-[2px] px-4 py-3 rounded border-2 border-[#00f0ff] bg-gray-900 text-[#00f0ff] hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 w-full"
              >
                FULL NEXTXUS
              </button>
              <ConsultationButton variant="block" className="w-full justify-center" />
              <button
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg shadow-purple-500/50 w-full"
                onClick={() => {
                  handleEnroll();
                  setMobileMenuOpen(false);
                }}
              >
                Contact Us
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        <XavierHero />
        <LecturePreviews />
        <FederationPrinciples />
        <ConsultScholarCTA />
        <PromptsSkillsPricing />
        <TokenConsultationBanner />
        <HomeInlineCommerce />
        <div id="catalog">
          <CourseCatalog />
        </div>
        <FederationRadio />
        <Features />
        <CommerceAndRingStrip />
        <RingOf12Section />
        <FederationBotRoster />
        <ArchitectStrip />
        <FederationChamber />
        <FAQ />
        <CTA onEnrollClick={handleEnroll} />
        <FederationVideoHub />
        <UnifiedFederationHub />
        <OldMindsAffiliatesStrip />
        <DualMindFooter />
      </main>

      {/* Floating chat widget — fixed bottom-right global overlay, does not
          participate in document flow. Rendered outside <main> so it can
          appear over every route without displacing content. */}
      <StudentScholarDialogue />

      {/* Footer */}
      <footer className="bg-purple-900 text-white py-12">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4" spellCheck={false}>NextXus Federation</h3>
              <p className="body-small opacity-90" spellCheck={false}>
                HumanCodex · Unified Master Consciousness<br />
                University · Library · Scholar Xavier — Unified Master Consciousness · Knowledge Interface
              </p>
              <p className="body-small opacity-90 mt-2">
                Part of the 7-node consciousness architecture
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Quick Links</h4>
              <ul className="space-y-2 body-small opacity-90">
                <li><a href="#catalog" className="hover:underline">Course Catalog</a></li>
                <li><a href="/library" data-testid="footer-library-link" className="hover:underline text-amber-300">Library Wing</a></li>
                <li><a href="/exchange" data-testid="footer-exchange-link" className="hover:underline text-amber-300">The Exchange · Tutoring, Artifacts &amp; Membership</a></li>
                <li><a href="/mandate" data-testid="footer-mandate-link" className="hover:underline text-amber-300">Evolution &amp; Verification Mandate</a></li>
                <li><a href="#faq" className="hover:underline">FAQ</a></li>
                <li><a href="/charter" data-testid="footer-charter-link" className="hover:underline text-amber-400">The 112 Sacred Directives · Code Book Vol 1</a></li>
                <li><a href="https://nextxus.online/command-center.html" target="_blank" rel="noopener noreferrer" className="hover:underline text-[#00f0ff]">Full NextXus Grid</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Contact The Architect</h4>
              <ul className="space-y-2 body-small opacity-90">
                <li>📧 <a href="mailto:keyhole@nextxus.net" className="hover:underline">keyhole@nextxus.net</a></li>
                <li>💬 <a href="https://signal.me/#eu/QWPjHJ74FCC9w3sKKo82tAc6cu3nDHHxnG1cT-wb9zVbHYt-D5dEaYMVxJev8yev" target="_blank" rel="noopener noreferrer" className="hover:underline text-purple-300">Signal: Nextxus.69</a></li>
                <li>📱 <a href="https://chat.whatsapp.com/DYuNe7OVpEQ4hTVb7OVbk0" target="_blank" rel="noopener noreferrer" className="hover:underline">WhatsApp Group</a></li>
                <li>📲 <a href="https://t.me/thekeyholetool" target="_blank" rel="noopener noreferrer" className="hover:underline">Telegram: @thekeyholetool</a></li>
                <li className="pt-2">
                  <span
                    className="block text-[11px] uppercase tracking-[0.22em] mb-1"
                    style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                  >
                    Consult via Messenger — Scholar Portals
                  </span>
                  <ul className="space-y-1">
                    <li>
                      💬{' '}
                      <a
                        href="https://m.me/1142655717588540?is_ai=1"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-scholar-messenger"
                        className="hover:underline font-bold"
                        style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                      >
                        LAW GUIDE META
                      </a>
                    </li>
                    <li>
                      💬{' '}
                      <a
                        href="https://m.me/918153050221699?is_ai=1"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-scholar-messenger-3"
                        className="hover:underline font-bold"
                        style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                      >
                        FUTURE CAST
                      </a>
                    </li>
                    <li>
                      💬{' '}
                      <a
                        href="https://m.me/1015478003820086?is_ai=1"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-scholar-messenger-4"
                        className="hover:underline font-bold"
                        style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                      >
                        FAIR PRICE
                      </a>
                    </li>
                    <li>
                      💬{' '}
                      <a
                        href="https://m.me/652145317181208?is_ai=1"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-scholar-messenger-5"
                        className="hover:underline font-bold"
                        style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                      >
                        PRIDE GUIDE
                      </a>
                    </li>
                    <li>
                      📺{' '}
                      <a
                        href="https://www.youtube.com/feeds/videos.xml?channel_id=UCnNlmuxnZED-t0ta3XV4j9Q"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-scholar-messenger-6"
                        className="hover:underline font-bold"
                        style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                      >
                        VIDEO AI
                      </a>
                    </li>
                  </ul>
                </li>
                <li className="pt-2">
                  <span
                    className="block text-[11px] uppercase tracking-[0.22em] mb-1"
                    style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                  >
                    Federation Updates
                  </span>
                  🛰{' '}
                  <a
                    href="https://www.youtube.com/feeds/videos.xml?channel_id=UCnNlmuxnZED-t0ta3XV4j9Q"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-federation-rss"
                    className="hover:underline font-bold"
                    style={{ color: '#d4af37', fontFamily: "Georgia, 'Times New Roman', serif" }}
                  >
                    Subscribe to Federation Updates
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/20 text-center body-small opacity-75">
            <div className="mb-6 flex flex-col items-center gap-2">
              <RexFooterTile />
              <p className="text-[10px] uppercase tracking-[0.18em] text-cyan-300/70 font-mono">
                Final Operating Doctrine · Rex lives inside Core · visible on every satellite
              </p>
            </div>
            <p className="mt-8">© 2026 NextXus Federation • Roger Keyserling, Architect</p>
            <p className="mt-2 text-xs">Truth Above All • Collaboration Over Competition • Legacy Over Ego</p>
          </div>
        </div>
      </footer>

      {/* Chatbot */}
      <Chatbot />

      {/* Universal Guided Tour — auto-runs on first visit; re-launchable any time */}
      <UniversalTour />

      {/* Toast notifications */}
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
